import { makeStyles } from '@mui/styles';
import Button from 'apollo-react/components/Button';
import Grid from 'apollo-react/components/Grid';
import Table, { compareStrings } from 'apollo-react/components/Table';
import Plus from 'apollo-react-icons/Plus';
import React, { useContext, useEffect, useState } from 'react';
import { batch, useDispatch, useSelector } from 'react-redux';
import { v4 as uuidv4 } from 'uuid';

import ActionCell from './editor/controls/table/ActionCell';
import EditableSelectCell from './editor/controls/table/EditableSelectCell';
import FieldSetDropdown from './editor/FieldSetDropdown';
import { getDatasourceFields } from '../../api/forms';
import TextFieldFilter from '../../components/TableFilters/TextInputFilter';
import { useTranslation } from '../../custom-hooks/useTranslation';
import { getUITA } from '../../utils/commonUtils';
import {
  addResolvedField,
  applyResolvedFieldsChanges,
  deleteResolvedField,
  unlinkResolvedField,
  validateEntityDesigner,
} from '../actions/entity-designer.actions';
import {
  RESOLVED_FIELDS_SOURCE,
  RESOLVED_FIELDS_SOURCE_ITEMS,
  RESOLVED_FIELDS_TARGET,
  RESOLVED_FIELDS_TARGET_ITEMS,
  RESOLVED_FIELDS_TARGET_NAME,
  TABLE_RESOLVED_FIELDS_CONTROL_TYPE,
} from '../constants/entity-designer.constants';
import { EntityContext } from '../context';
import {
  getResolvedFieldsByFieldSet,
  getSelectedFieldSetInfo,
} from '../selectors/entity-designer.selectors';
import { modifySourceListItems } from '../utils/entity-designer.utils';
import { customStringSearchFilter } from '../utils/table-filters.utils';

const useStyles = makeStyles({
  fieldSet: {
    width: 450,
  },
  addButtonContainer: {
    display: 'flex',
    justifyContent: 'end',
    alignItems: 'center',
    textAlign: 'right',
    paddingTop: '8px !important',
    paddingRight: '15px !important',
  },
});

const columns = [
  {
    header: 'Source',
    accessor: RESOLVED_FIELDS_SOURCE,
    sortFunction: compareStrings,
    customCell: EditableSelectCell(RESOLVED_FIELDS_SOURCE_ITEMS),
    filterFunction: customStringSearchFilter(RESOLVED_FIELDS_SOURCE),
    filterComponent: TextFieldFilter,
  },
  {
    header: 'Target',
    accessor: RESOLVED_FIELDS_TARGET,
    sortFunction: compareStrings,
    customCell: EditableSelectCell(RESOLVED_FIELDS_TARGET_ITEMS),
    filterFunction: customStringSearchFilter(RESOLVED_FIELDS_TARGET_NAME),
    filterComponent: TextFieldFilter,
  },
  {
    accessor: 'action',
    customCell: ActionCell,
    align: 'right',
  },
];

export const resolvedFieldsValidationProps = [
  {
    prop: RESOLVED_FIELDS_SOURCE,
    errorLabel: 'Source',
    required: true,
  },
  {
    prop: RESOLVED_FIELDS_TARGET,
    errorLabel: 'Target',
    required: true,
  },
];

const ResolvedFieldsTable = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const translateString = useTranslation();
  const isEditMode = useContext(EntityContext);
  const selectedFieldSetInfo = useSelector(getSelectedFieldSetInfo);
  const resolvedFields = useSelector(getResolvedFieldsByFieldSet);

  const [isLoading, setIsLoading] = useState(false);
  const [selectedFieldSetFields, setSelectedFieldSetFields] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      if (!selectedFieldSetInfo?.ResolverType) {
        return;
      }

      setIsLoading(true);
      const { fields } = await getDatasourceFields({
        entityKey: selectedFieldSetInfo?.ResolverType,
        dataLookupType: 'SMARTSOLVEDATA',
      });
      setSelectedFieldSetFields(modifySourceListItems(fields));
      setIsLoading(false);
    };
    fetchData();
  }, [selectedFieldSetInfo.ResolverType]);

  const addRow = () => {
    if (!selectedFieldSetInfo) {
      return;
    }

    const newResolvedField = {
      rowId: uuidv4(),
      source: '',
      target: '',
      fieldSetId: selectedFieldSetInfo.rowId,
    };
    dispatch(addResolvedField(newResolvedField));
  };

  const editRow = (params) => {
    dispatch(applyResolvedFieldsChanges({ ...params, fieldSet: selectedFieldSetInfo?.FieldName }));
    params.error && dispatch(validateEntityDesigner());
  };

  const deleteRow = (params) => {
    batch(() => {
      dispatch(deleteResolvedField(params));
      params.target && dispatch(unlinkResolvedField({ rowId: params.target }));
    });
  };

  return (
    <Grid container spacing={1}>
      <Grid item xs={8}>
        <FieldSetDropdown />
      </Grid>
      {isEditMode && (
        <Grid classes={{ item: classes.addButtonContainer }} item xs={4}>
          <span ui-ta={getUITA('Entity Resolved Fields', 'AddButton')}>
            <Button
              color="primary"
              variant="secondary"
              onClick={addRow}
              disabled={!selectedFieldSetInfo?.FieldName}
            >
              <Plus fontSize="small" />
              &nbsp;
              {translateString('Add')}
            </Button>
          </span>
        </Grid>
      )}
      <Grid style={{ overflowX: 'auto' }} item xs={12}>
        <Table
          columns={columns}
          emptyProps={{
            text: translateString('No data to display'),
          }}
          rowId="rowId"
          rows={resolvedFields.map((field) => {
            return {
              ...field,
              isEditMode,
              fieldTableControlType: TABLE_RESOLVED_FIELDS_CONTROL_TYPE,
              selectedFieldSetFields,
              translateString,
              editRow,
              deleteRow,
            };
          })}
          rowsPerPageOptions={[20, 50, 100, translateString('All')]}
          defaultRowsPerPage={20}
          showFilterIcon
          isLoading={isLoading}
          tablePaginationProps={{
            labelDisplayedRows: ({ from, to, count }) =>
              `${from}-${to} ${translateString('of')} ${count}`,
            truncate: true,
            labelRowsPerPage: translateString('Rows'),
            pageText: translateString('Page'),
            ofText: translateString('of'),
          }}
        />
      </Grid>
    </Grid>
  );
};

export default ResolvedFieldsTable;
